/**
 * Queue Management React Query Hooks
 * 
 * Provides hooks for queue management with automatic caching,
 * optimistic updates, and cache invalidation.
 * 
 * Part of the 3-layer architecture refactor
 * Repository → Service → React Query Hooks → Components
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queueService } from '../services';
import type {
  CallQueue,
  AgentQueueAssignment,
  PhoneQueueAssignment,
  CallCenterAgent
} from '../repositories/QueueRepository';

// ==================== QUERY KEYS ====================

export const queueKeys = {
  all: ['queues'] as const,
  lists: () => [...queueKeys.all, 'list'] as const,
  list: (filters?: any) => [...queueKeys.lists(), filters] as const,
  details: () => [...queueKeys.all, 'detail'] as const,
  detail: (id: string) => [...queueKeys.details(), id] as const,
  stats: () => [...queueKeys.all, 'stats'] as const,
  queueStats: (queueName: string) => [...queueKeys.stats(), queueName] as const,
  agents: () => [...queueKeys.all, 'agents'] as const,
  queueAgents: (queueId: string) => [...queueKeys.agents(), queueId] as const,
  phones: () => [...queueKeys.all, 'phones'] as const,
  queuePhones: (queueId: string) => [...queueKeys.phones(), queueId] as const,
  allAgents: ['agents'] as const,
  voicemail: () => [...queueKeys.all, 'voicemail'] as const,
  queueVoicemail: (queueId: string) => [...queueKeys.voicemail(), queueId] as const,
};

// ==================== QUERY HOOKS ====================

/**
 * Get all queues
 */
export function useQueues(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.list(),
    queryFn: () => queueService.getAllQueues(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Get active queues only
 */
export function useActiveQueues(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.list({ active: true }),
    queryFn: () => queueService.getActiveQueues(),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

/**
 * Get queue by ID
 */
export function useQueue(queueId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.detail(queueId),
    queryFn: () => queueService.getQueue(queueId),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled !== false && !!queueId,
  });
}

/**
 * Get queue with detailed stats
 */
export function useQueueDetails(queueId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...queueKeys.detail(queueId), 'details'],
    queryFn: () => queueService.getQueueWithDetails(queueId),
    staleTime: 1 * 60 * 1000, // 1 minute (stats change frequently)
    enabled: options?.enabled !== false && !!queueId,
  });
}

/**
 * Get queue statistics for today
 */
export function useQueueStats(queueName: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.queueStats(queueName),
    queryFn: () => queueService.getQueueStats(queueName),
    staleTime: 5 * 1000, // refresh fast for near-real-time dashboard
    refetchInterval: 5 * 1000, // Auto-refresh every 5 seconds
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: true,
    enabled: options?.enabled !== false && !!queueName,
  });
}

/**
 * Get all agents
 */
export function useAllAgents(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.allAgents,
    queryFn: () => queueService.getAllAgents(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Get agents assigned to a queue
 */
export function useQueueAgents(queueId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.queueAgents(queueId),
    queryFn: () => queueService.getQueueAgents(queueId),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled !== false && !!queueId,
  });
}

/**
 * Get phone assignments for a queue
 */
export function useQueuePhones(queueId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.queuePhones(queueId),
    queryFn: () => queueService.getQueuePhones(queueId),
    staleTime: 2 * 60 * 1000,
    enabled: options?.enabled !== false && !!queueId,
  });
}

/**
 * Get all queue stats
 */
export function useAllQueueStats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.stats(),
    queryFn: () => queueService.getAllQueueStats(),
    staleTime: 5 * 1000,
    refetchInterval: 5 * 1000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: true,
    ...options,
  });
}

/**
 * Get all phone number assignments across all queues
 */
export function useAllPhones(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.phones(),
    queryFn: () => queueService.getAllPhones(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

// ==================== MUTATION HOOKS ====================

/**
 * Create a new queue
 */
export function useCreateQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof queueService.createQueue>[0]) =>
      queueService.createQueue(input),
    onSuccess: () => {
      // Invalidate queue lists
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

/**
 * Update queue configuration
 */
export function useUpdateQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueId, updates }: {
      queueId: string;
      updates: Parameters<typeof queueService.updateQueue>[1];
    }) => queueService.updateQueue(queueId, updates),
    onSuccess: (_, variables) => {
      // Invalidate specific queue and lists
      queryClient.invalidateQueries({ queryKey: queueKeys.detail(variables.queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

/**
 * Toggle queue active status
 */
export function useToggleQueueStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueId: string) => queueService.toggleQueueStatus(queueId),
    onMutate: async (queueId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queueKeys.detail(queueId) });

      // Snapshot previous value
      const previousQueue = queryClient.getQueryData<CallQueue>(queueKeys.detail(queueId));

      // Optimistically update
      if (previousQueue) {
        queryClient.setQueryData<CallQueue>(queueKeys.detail(queueId), {
          ...previousQueue,
          is_active: !previousQueue.is_active,
        });
      }

      return { previousQueue };
    },
    onError: (_err, queueId, context) => {
      // Rollback on error
      if (context?.previousQueue) {
        queryClient.setQueryData(queueKeys.detail(queueId), context.previousQueue);
      }
    },
    onSettled: (_data, _error, queueId) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queueKeys.detail(queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

/**
 * Delete a queue
 */
export function useDeleteQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (queueId: string) => queueService.deleteQueue(queueId),
    onSuccess: (_data, queueId) => {
      // Remove from cache and invalidate lists
      queryClient.removeQueries({ queryKey: queueKeys.detail(queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

/**
 * Assign agent to queue
 */
export function useAssignAgentToQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, queueId, priority }: {
      agentId: string;
      queueId: string;
      priority?: number;
    }) => queueService.assignAgentToQueue(agentId, queueId, priority),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so we don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: queueKeys.queueAgents(variables.queueId) });

      // Snapshot the previous value
      const previous = queryClient.getQueryData<any[]>(queueKeys.queueAgents(variables.queueId)) || [];

      // Optimistically update to the new value
      const optimisticAssignment = {
        id: `optimistic-${variables.agentId}-${variables.queueId}`,
        agent_id: null,
        queue_id: variables.queueId,
        priority: variables.priority ?? 50,
        created_at: new Date().toISOString(),
        agent_user_id: variables.agentId, // critical: UI compares against user.id
      };

      queryClient.setQueryData<any[]>(queueKeys.queueAgents(variables.queueId), [...previous, optimisticAssignment]);

      // Return context with the previous data to rollback on error
      return { previous };
    },
    onError: (_err, variables, context) => {
      // Rollback to previous data
      if (context?.previous) {
        queryClient.setQueryData(queueKeys.queueAgents(variables.queueId), context.previous);
      }
    },
    onSuccess: (_data, variables) => {
      // Ensure fresh data after server confirms
      queryClient.invalidateQueries({ queryKey: queueKeys.queueAgents(variables.queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.detail(variables.queueId) });
    },
    onSettled: (_data, _error, variables) => {
      // Final refetch to reconcile any optimistic artifacts
      queryClient.invalidateQueries({ queryKey: queueKeys.queueAgents(variables.queueId) });
    },
  });
}

/**
 * Remove agent from queue
 */
export function useRemoveAgentFromQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assignmentId, queueId }: {
      assignmentId: string;
      queueId: string;
    }) => queueService.removeAgentFromQueue(assignmentId),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queueKeys.queueAgents(variables.queueId) });
      const previous = queryClient.getQueryData<any[]>(queueKeys.queueAgents(variables.queueId)) || [];

      // Optimistically remove from cache (handles both real and optimistic ids)
      queryClient.setQueryData<any[]>(
        queueKeys.queueAgents(variables.queueId),
        previous.filter(a => a.id !== variables.assignmentId)
      );

      return { previous };
    },
    onError: (_err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queueKeys.queueAgents(variables.queueId), context.previous);
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queueKeys.queueAgents(variables.queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.detail(variables.queueId) });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: queueKeys.queueAgents(variables.queueId) });
    },
  });
}

/**
 * Assign phone to queue
 */
export function useAssignPhoneToQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ phoneNumber, queueId, isPrimary }: {
      phoneNumber: string;
      queueId: string;
      isPrimary?: boolean;
    }) => queueService.assignPhoneToQueue(phoneNumber, queueId, isPrimary),
    onSuccess: (_data, variables) => {
      // Invalidate queue phones
      queryClient.invalidateQueries({ queryKey: queueKeys.queuePhones(variables.queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.detail(variables.queueId) });
    },
  });
}

/**
 * Remove phone from queue
 */
export function useRemovePhoneFromQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assignmentId, queueId }: {
      assignmentId: string;
      queueId: string;
    }) => queueService.removePhoneFromQueue(assignmentId),
    onSuccess: (_data, variables) => {
      // Invalidate queue phones
      queryClient.invalidateQueries({ queryKey: queueKeys.queuePhones(variables.queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.detail(variables.queueId) });
    },
  });
}

/**
 * Update routing policy for a queue
 */
export function useUpdateQueueRoutingPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queueId, policy }: {
      queueId: string;
      policy: Parameters<typeof queueService.updateQueueRoutingPolicy>[1];
    }) => queueService.updateQueueRoutingPolicy(queueId, policy),
    onSuccess: (_data, variables) => {
      // Invalidate queue details and lists
      queryClient.invalidateQueries({ queryKey: queueKeys.detail(variables.queueId) });
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

/**
 * Get queue voicemail settings
 */
export function useQueueVoicemailSettings(queueId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queueKeys.queueVoicemail(queueId),
    queryFn: () => queueService.getQueueVoicemailSettings(queueId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options?.enabled !== false && !!queueId,
  });
}

/**
 * Upsert queue voicemail settings
 */
export function useUpsertQueueVoicemailSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof queueService.upsertQueueVoicemailSettings>[0]) =>
      queueService.upsertQueueVoicemailSettings(input),
    onSuccess: (_data, input) => {
      // Invalidate voicemail settings for this queue
      queryClient.invalidateQueries({ queryKey: queueKeys.queueVoicemail(input.queue_id) });
    },
  });
}
